import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Users,
  CheckCircle2,
  Clock,
  ChevronRight,
  Shield,
  History,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { useDocumentTitle } from 'usehooks-ts';
import { toast } from 'sonner';
import { useState } from 'react';
import { format } from 'date-fns';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import MainHeader from '@/components/layout/MainHeader';
import { Main } from '@/components/main';
import type { PolicySummaryDTO, PolicyAcknowledgmentDTO } from '@deepguide-ai/types';

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/compliance/policies/'
)({
  component: PoliciesPage,
});

// Policy type display names
const POLICY_TYPE_LABELS: Record<string, string> = {
  INFORMATION_SECURITY: 'Information Security',
  ACCEPTABLE_USE: 'Acceptable Use',
  ACCESS_CONTROL: 'Access Control',
  DATA_CLASSIFICATION: 'Data Classification',
  INCIDENT_RESPONSE: 'Incident Response',
  BUSINESS_CONTINUITY: 'Business Continuity',
  CHANGE_MANAGEMENT: 'Change Management',
  VENDOR_MANAGEMENT: 'Vendor Management',
  ENCRYPTION: 'Encryption',
  PASSWORD: 'Password',
  REMOTE_ACCESS: 'Remote Access',
  PHYSICAL_SECURITY: 'Physical Security',
  PRIVACY: 'Privacy',
  EMPLOYEE_HANDBOOK: 'Employee Handbook',
  OTHER: 'Other',
};

interface PolicyCardProps {
  policy: PolicySummaryDTO;
  onAcknowledge: () => void;
  onViewDetails: () => void;
}

function PolicyCard({ policy, onAcknowledge, onViewDetails }: PolicyCardProps) {
  const ackRate = policy.acknowledgmentRate;
  const isFullyAcknowledged = ackRate >= 100;

  return (
    <div className="relative group w-full text-left rounded-xl border bg-card hover:border-primary/30 transition-all shadow-sm hover:shadow-md">
      <button
        onClick={onViewDetails}
        className="absolute inset-0 rounded-xl z-0"
        aria-label={`View ${policy.title}`}
      />

      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-lg shrink-0 ${isFullyAcknowledged ? 'bg-green-500/10 text-green-500' : 'bg-blue-500/10 text-blue-500'}`}>
            <FileText className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold truncate">{policy.title}</h3>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-medium">
                {POLICY_TYPE_LABELS[policy.policyType] || policy.policyType}
              </Badge>
              <span className="text-xs text-muted-foreground">
                v{policy.currentVersion}
              </span>
            </div>
          </div>
        </div>

        {/* Acknowledgment Stats */}
        <div className="mt-4 flex items-center justify-between py-3 px-4 rounded-lg bg-muted/30">
          <div className="text-center flex-1">
            <div className="flex items-center justify-center gap-1">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-lg font-semibold tabular-nums">
                {policy.totalUsers}
              </span>
            </div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">
              Total Users
            </div>
          </div>
          <div className="h-8 w-px bg-border" />
          <div className="text-center flex-1">
            <div className="flex items-center justify-center gap-1">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="text-lg font-semibold tabular-nums text-green-600 dark:text-green-500">
                {policy.acknowledgedCount}
              </span>
            </div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">
              Acknowledged
            </div>
          </div>
          <div className="h-8 w-px bg-border" />
          <div className="text-center flex-1">
            <div className="flex items-center justify-center gap-1">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-lg font-semibold tabular-nums text-amber-600 dark:text-amber-500">
                {policy.pendingCount}
              </span>
            </div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">
              Pending
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 flex items-center gap-3">
          <Progress value={ackRate} className="h-1.5 flex-1" />
          <span className="text-xs font-medium tabular-nums text-muted-foreground w-10 text-right">
            {ackRate.toFixed(0)}%
          </span>
        </div>

        {/* Actions */}
        <div className="mt-3 flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            className="relative z-10"
            onClick={(e) => {
              e.stopPropagation();
              onAcknowledge();
            }}
          >
            Acknowledge
          </Button>
          <div className="flex items-center text-xs text-muted-foreground group-hover:text-primary transition-colors">
            View details <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </div>
        </div>
      </div>
    </div>
  );
}

interface PolicyDetailDialogProps {
  policy: PolicySummaryDTO | null;
  isOpen: boolean;
  onClose: () => void;
  onAcknowledge: () => void;
  isAcknowledging: boolean;
}

function PolicyDetailDialog({
  policy,
  isOpen,
  onClose,
  onAcknowledge,
  isAcknowledging,
}: PolicyDetailDialogProps) {
  if (!policy) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{policy.title}</DialogTitle>
          <DialogDescription>
            Version {policy.currentVersion}
            {policy.effectiveDate && ` • Effective ${policy.effectiveDate}`}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Policy Type */}
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {POLICY_TYPE_LABELS[policy.policyType] || policy.policyType}
            </Badge>
            {policy.frameworkName && (
              <Badge variant="secondary">{policy.frameworkName}</Badge>
            )}
          </div>

          {/* Statistics */}
          <div className="rounded-lg border p-4">
            <h4 className="text-sm font-medium mb-3">Acknowledgment Status</h4>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-semibold tabular-nums text-green-600">
                  {policy.acknowledgedCount}
                </div>
                <div className="text-xs text-muted-foreground">Acknowledged</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold tabular-nums text-amber-600">
                  {policy.pendingCount}
                </div>
                <div className="text-xs text-muted-foreground">Pending</div>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">{policy.acknowledgmentRate.toFixed(0)}%</span>
                </div>
                <Progress value={policy.acknowledgmentRate} className="h-2" />
              </div>
            </div>
          </div>

          {/* Agreement text */}
          <div className="rounded-lg border p-4 bg-muted/30">
            <p className="text-sm text-muted-foreground">
              By clicking "Acknowledge", you confirm that you have read, understood, and agree to comply with this policy. Your acknowledgment will be recorded with a timestamp.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onAcknowledge} disabled={isAcknowledging}>
            {isAcknowledging ? 'Acknowledging...' : 'Acknowledge Policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// My Acknowledgments History component
interface MyAcknowledgmentsProps {
  workspaceId: string;
}

function MyAcknowledgments({ workspaceId }: MyAcknowledgmentsProps) {
  const navigate = useNavigate();
  const myAcknowledgmentsQuery = useQuery(trpc.policy.myAcknowledgments.queryOptions());

  const acknowledgments = myAcknowledgmentsQuery.data ?? [];

  if (myAcknowledgmentsQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (acknowledgments.length === 0) {
    return (
      <div className="text-center py-12 border rounded-xl bg-muted/20">
        <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <div className="text-muted-foreground mb-2">No acknowledgments yet</div>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          When you acknowledge policies, they will appear here with timestamps.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Policy</TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Acknowledged At</TableHead>
            <TableHead className="w-[100px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {acknowledgments.map((ack) => (
            <TableRow key={ack.id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{ack.policyTitle || 'Unknown Policy'}</span>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="font-mono">
                  v{ack.version}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {format(new Date(ack.acknowledgedAt), 'MMM d, yyyy h:mm a')}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    navigate({
                      to: '/workspaces/$workspaceId/compliance/tests/$testId',
                      params: { workspaceId, testId: ack.templateId },
                    })
                  }
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Policies requiring acknowledgment component
interface PendingPoliciesProps {
  policies: PolicySummaryDTO[];
  onAcknowledge: (policy: PolicySummaryDTO) => void;
  onViewDetails: (policy: PolicySummaryDTO) => void;
}

function PendingPolicies({ policies, onAcknowledge, onViewDetails }: PendingPoliciesProps) {
  // Filter to policies where user hasn't acknowledged (pendingCount > 0 means some users haven't)
  // For a proper portal, we'd check current user's acknowledgment status
  // For now, we show all policies that require acknowledgment tracking
  const pendingPolicies = policies.filter(p => p.pendingCount > 0);

  if (pendingPolicies.length === 0) {
    return (
      <div className="text-center py-12 border rounded-xl bg-green-50 dark:bg-green-950/20">
        <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
        <div className="text-green-700 dark:text-green-400 font-medium mb-2">
          All policies acknowledged!
        </div>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          You're up to date with all organizational policies.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 shrink-0" />
        <p className="text-sm text-amber-700 dark:text-amber-400">
          <strong>{pendingPolicies.length}</strong> {pendingPolicies.length === 1 ? 'policy requires' : 'policies require'} your acknowledgment
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {pendingPolicies.map((policy) => (
          <PolicyCard
            key={policy.templateId}
            policy={policy}
            onAcknowledge={() => onAcknowledge(policy)}
            onViewDetails={() => onViewDetails(policy)}
          />
        ))}
      </div>
    </div>
  );
}

function PoliciesPage() {
  useDocumentTitle('Policy Acknowledgment Portal - Compliance');

  const { workspaceId } = Route.useParams();
  const queryClient = useQueryClient();

  // Tab state
  const [activeTab, setActiveTab] = useState<'pending' | 'all' | 'history'>('pending');

  // Dialog state
  const [selectedPolicy, setSelectedPolicy] = useState<PolicySummaryDTO | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  // Queries
  const policiesQuery = useQuery(trpc.policy.list.queryOptions({}));

  // Mutations
  const acknowledgeMutation = useMutation({
    ...trpc.policy.acknowledge.mutationOptions(),
    onSuccess: () => {
      toast.success('Policy acknowledged successfully');
      queryClient.invalidateQueries({
        queryKey: trpc.policy.list.queryKey({}),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.policy.myAcknowledgments.queryKey(),
      });
      setIsDetailDialogOpen(false);
      setSelectedPolicy(null);
    },
    onError: (error) => {
      toast.error('Failed to acknowledge policy');
      console.error('Failed to acknowledge policy:', error);
    },
  });

  // Handlers
  const handleAcknowledge = (policy: PolicySummaryDTO) => {
    setSelectedPolicy(policy);
    setIsDetailDialogOpen(true);
  };

  const handleViewDetails = (policy: PolicySummaryDTO) => {
    setSelectedPolicy(policy);
    setIsDetailDialogOpen(true);
  };

  const handleConfirmAcknowledge = () => {
    if (!selectedPolicy) return;
    acknowledgeMutation.mutate({
      templateId: selectedPolicy.templateId,
    });
  };

  const policies = policiesQuery.data?.items ?? [];
  const pendingCount = policies.filter(p => p.pendingCount > 0).length;

  return (
    <Main>
      <MainHeader />

      <div className="mx-auto box-content flex max-w-[1008px] flex-col px-6 pb-12">
        {/* Header section */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="text-foreground text-2xl leading-6 font-semibold">
                Policy Acknowledgment Portal
              </div>
              <div className="text-muted-foreground text-sm leading-5">
                Review and acknowledge organizational policies for SOC 2 compliance
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Pending
              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 px-1.5">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              All Policies
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              My Acknowledgments
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {policiesQuery.isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Skeleton className="h-52 w-full" />
                <Skeleton className="h-52 w-full" />
              </div>
            ) : (
              <PendingPolicies
                policies={policies}
                onAcknowledge={handleAcknowledge}
                onViewDetails={handleViewDetails}
              />
            )}
          </TabsContent>

          <TabsContent value="all">
            {policiesQuery.isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Skeleton className="h-52 w-full" />
                <Skeleton className="h-52 w-full" />
                <Skeleton className="h-52 w-full" />
                <Skeleton className="h-52 w-full" />
              </div>
            ) : policies.length === 0 ? (
              <div className="text-center py-12 border rounded-xl bg-muted/20">
                <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <div className="text-muted-foreground mb-4">No policies configured yet</div>
                <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                  Policies are created from POLICY-type templates. Create a policy template to track user acknowledgments.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {policies.map((policy) => (
                  <PolicyCard
                    key={policy.templateId}
                    policy={policy}
                    onAcknowledge={() => handleAcknowledge(policy)}
                    onViewDetails={() => handleViewDetails(policy)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <MyAcknowledgments workspaceId={workspaceId} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Policy Detail Dialog */}
      <PolicyDetailDialog
        policy={selectedPolicy}
        isOpen={isDetailDialogOpen}
        onClose={() => {
          setIsDetailDialogOpen(false);
          setSelectedPolicy(null);
        }}
        onAcknowledge={handleConfirmAcknowledge}
        isAcknowledging={acknowledgeMutation.isPending}
      />
    </Main>
  );
}
